import { Input } from 'src/components/widgets/input';
import { AdvancedSelect } from '../../../widgets/select';
import { Dialog, DialogWindow, useDialogManager } from '../../../widgets/dialog';
import { FormControl } from '../../../widgets/formControl';
import { NotificationBanner } from '../../../widgets/notificationBanner';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { Radio } from '../../../widgets/radio';
import { SubSection } from '../../../widgets/section';
import Stack from '../../../widgets/stack/stack';
import React, { FC, useCallback, useState } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay/hooks';
import { commitMutation, fetchQuery } from 'relay-runtime';
import { salesChannelsLabels } from 'src/components/pages/products/utils';
import { T, toLocalID } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { createStandaloneSiteDialogCheckDomainQuery } from './__generated__/createStandaloneSiteDialogCheckDomainQuery.graphql';
import { createStandaloneSiteDialogForkThemeMutation } from './__generated__/createStandaloneSiteDialogForkThemeMutation.graphql';
import { createStandaloneSiteDialogMarketplaceQuery } from './__generated__/createStandaloneSiteDialogMarketplaceQuery.graphql';
import { createStandaloneSiteDialogMutation } from './__generated__/createStandaloneSiteDialogMutation.graphql';
import { getStandaloneSiteSubdomain } from './standaloneSiteNavigation';

const StyledDialogWindow = styled(DialogWindow)`
    max-width: 600px;

    > div:nth-child(3) {
        overflow-y: visible;
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
    margin: 4px 0;
`;

interface SalesChannel {
    readonly id: string;
    readonly detail: {
        readonly id: string;
        readonly properties: {
            readonly name: string | null;
        } | null;
    } | null;
    readonly ecommerceSettings: {
        readonly id: string;
        readonly ecommerceName: string;
        readonly definitiveUrl: string | null;
        readonly remoteUrl: string | null;
    } | null;
}

interface CreateStandaloneSiteDialogProps {
    salesChannels: readonly SalesChannel[];
    onCreated: (site: { id: string; standaloneSiteID: number }) => void;
}

const NAME_VALIDATION_KEY = 'createStandaloneSiteName';
const DOMAIN_VALIDATION_KEY = 'createStandaloneSiteDomain';
const SALES_CHANNEL_VALIDATION_KEY = 'createStandaloneSiteSalesChannel';

const checkDomainQuery = graphql`
    query createStandaloneSiteDialogCheckDomainQuery($domain: String!) {
        checkStandaloneSiteDomain(domain: $domain) {
            isAvailable
            message
        }
    }
`;

const marketplaceThemesQuery = graphql`
    query createStandaloneSiteDialogMarketplaceQuery {
        marketplaceThemes {
            marketplaceThemeID
            name
        }
    }
`;

const forkThemeMutation = graphql`
    mutation createStandaloneSiteDialogForkThemeMutation($input: StandaloneSiteForkMarketplaceThemeInput!) {
        forkMarketplaceTheme(input: $input) {
            id
            standaloneSiteThemeID
            status
        }
    }
`;

const CreateStandaloneSiteDialogContent: FC<CreateStandaloneSiteDialogProps> = ({ salesChannels, onCreated }) => {
    const { closeDialog } = useDialogManager();
    const environment = useRelayEnvironment();
    const validationContext = useValidationContext();
    const [name, setName] = useState('');
    const [domain, setDomain] = useState('');
    const [productSource, setProductSource] = useState<'scratch' | 'channel'>('scratch');
    const [selectedSalesChannelId, setSelectedSalesChannelId] = useState<string | null>(salesChannels.length === 1 ? salesChannels[0].id : null);
    const [domainError, setDomainError] = useState<string | null>(null);
    const [isCheckingDomain, setIsCheckingDomain] = useState(false);
    // Mini-Sites are currently available for Enterprise customers only; creation is not saveable.
    const isEnterpriseLocked = true;

    const [createSite, isCreating] = useMutation<createStandaloneSiteDialogMutation>(graphql`
        mutation createStandaloneSiteDialogMutation($input: StandaloneSiteCreateInput!) {
            createStandaloneSite(input: $input) {
                id
                standaloneSiteID
                name
                domain
                status
            }
        }
    `);

    const scratchChannel = salesChannels.find((sc) => sc.ecommerceSettings?.ecommerceName === 'NONE');
    const linkedChannels = salesChannels.filter((sc) => sc.ecommerceSettings?.ecommerceName !== 'NONE');

    const salesChannelOptions = linkedChannels.map((sc) => {
        const name = sc.detail?.properties?.name ? sc.detail.properties.name : salesChannelsLabels(sc.ecommerceSettings?.ecommerceName ?? '', true);
        const url = sc.ecommerceSettings?.definitiveUrl || sc.ecommerceSettings?.remoteUrl;
        return {
            value: sc.id,
            label: url ? `${name} (${url})` : name
        };
    });

    const handleDomainChange = useCallback(
        (value: string) => {
            setDomain(value);
            setDomainError(null);
            validationContext.update(DOMAIN_VALIDATION_KEY, undefined);
        },
        [validationContext]
    );

    const resolvedChannelId = productSource === 'scratch' ? scratchChannel?.id : selectedSalesChannelId;

    const handleCreate = async () => {
        if (!validationContext.submit()) return;

        const normalizedDomain = getStandaloneSiteSubdomain(domain);

        if (productSource === 'scratch' && !scratchChannel?.id)
            showErrorNotification({ message: T._('No from-scratch channel available. Please contact support.', 'Admin') });

        if (!normalizedDomain || (productSource === 'scratch' && !scratchChannel?.id)) return;

        const salesChannelLocalId = toLocalID(resolvedChannelId!);
        if (salesChannelLocalId == null) {
            showErrorNotification({ message: T._('Please select a valid online store.', 'Admin') });
            return;
        }

        // Check domain availability before creating
        setIsCheckingDomain(true);
        try {
            const result = await fetchQuery<createStandaloneSiteDialogCheckDomainQuery>(environment, checkDomainQuery, {
                domain: normalizedDomain
            }).toPromise();

            if (!result?.checkStandaloneSiteDomain?.isAvailable) {
                const message = result?.checkStandaloneSiteDomain?.message || T._('This domain is not available.', 'Admin');
                setDomainError(message);
                validationContext.update(DOMAIN_VALIDATION_KEY, message);
                validationContext.submit();
                setIsCheckingDomain(false);
                return;
            }
        } catch {
            showErrorNotification({ message: T._('Failed to validate domain.', 'Admin') });
            setIsCheckingDomain(false);
            return;
        }
        setIsCheckingDomain(false);

        createSite({
            variables: {
                input: {
                    name: name.trim(),
                    domain: normalizedDomain,
                    salesChannelID: salesChannelLocalId
                }
            },
            onCompleted: (response) => {
                showSuccessNotification({ message: T._('Mini-Site site created.', 'Admin') });

                const created = response.createStandaloneSite;

                // Assign default marketplace theme
                const siteId = created?.id;
                if (siteId) {
                    fetchQuery<createStandaloneSiteDialogMarketplaceQuery>(environment, marketplaceThemesQuery, {})
                        .toPromise()
                        .then((result) => {
                            const firstTheme = result?.marketplaceThemes?.[0];
                            if (firstTheme) {
                                commitMutation<createStandaloneSiteDialogForkThemeMutation>(environment, {
                                    mutation: forkThemeMutation,
                                    variables: {
                                        input: {
                                            standaloneSiteId: siteId,
                                            marketplaceThemeID: firstTheme.marketplaceThemeID
                                        }
                                    }
                                });
                            }
                        });
                }

                if (created) {
                    onCreated({ id: created.id, standaloneSiteID: created.standaloneSiteID });
                }
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed to create Mini-Site site.', 'Admin') });
            },
            updater: (store) => store.invalidateStore()
        });
    };

    return (
        <Dialog
            windowDecorator={StyledDialogWindow}
            title={T._('Create Mini-Site', 'Admin')}
            buttons={[
                {
                    label: isCheckingDomain ? T._('Checking domain...', 'Admin') : T._('Create', 'Admin'),
                    onClick: handleCreate,
                    // Mini-Sites are an Enterprise-only feature; saving is disabled for everyone else.
                    disabled: isEnterpriseLocked || isCreating || isCheckingDomain
                }
            ]}
            onClose={() => closeDialog('create-standalone-site')}
        >
            <Stack $gap='16px'>
                <NotificationBanner
                    type='EXTRA'
                    content={
                        <>
                            {T._("Mini-Sites are available only for Enterprise customers. If you're interested in this feature, contact ", 'Admin')}
                            <a href='mailto:help@zakeke.com'>help@zakeke.com</a>
                            {T._('.', 'Admin')}
                        </>
                    }
                />
                <FormControl label={T._('Name', 'Admin')} caption={T._('A display name to identify this portal in your dashboard.', 'Admin')}>
                    <Input
                        value={name}
                        onChange={(e) => {
                            setName(e.currentTarget.value);
                        }}
                        placeholder={T._('Enter site name', 'Admin')}
                        validationKey={NAME_VALIDATION_KEY}
                        validationLabel={T._('Name', 'Admin')}
                        validate={validateRequiredTrimmed}
                        validationMessage={T._('Name is required.', 'Admin')}
                        disabled={isEnterpriseLocked}
                    />
                </FormControl>

                <FormControl label={T._('Domain', 'Admin')} caption={T._('Your site will be available at your-domain.customhub.app', 'Admin')}>
                    <Input
                        value={domain}
                        onChange={(e) => handleDomainChange(e.currentTarget.value)}
                        placeholder={T._('Enter subdomain', 'Admin')}
                        validationKey={DOMAIN_VALIDATION_KEY}
                        validationLabel={T._('Domain', 'Admin')}
                        validate={(value) => !!domainError || !getStandaloneSiteSubdomain(value)}
                        validationMessage={domainError || T._('Domain is required.', 'Admin')}
                        disabled={isEnterpriseLocked}
                    />
                </FormControl>

                <Divider />

                <SubSection title={T._('Product source', 'Admin')} contentGap='8px'>
                    <Radio
                        id='source-scratch'
                        name='productSource'
                        label={T._('From scratch', 'Admin')}
                        caption={T._('Use products created directly in Zakeke, not linked to any e-commerce.', 'Admin')}
                        value='scratch'
                        checked={productSource === 'scratch'}
                        onSelect={() => setProductSource('scratch')}
                        disabled={isEnterpriseLocked}
                    />
                    <Radio
                        id='source-channel'
                        name='productSource'
                        label={T._('From an online store', 'Admin')}
                        caption={T._('Use products imported from an existing online store (e.g. Shopify).', 'Admin')}
                        value='channel'
                        checked={productSource === 'channel'}
                        onSelect={() => setProductSource('channel')}
                        disabled={isEnterpriseLocked}
                    />
                    <div style={{ visibility: productSource === 'channel' ? 'visible' : 'hidden' }}>
                        <AdvancedSelect
                            placeholder={T._('Select an online store', 'Admin')}
                            options={salesChannelOptions}
                            value={salesChannelOptions.find((o) => o.value === selectedSalesChannelId) || null}
                            onChange={(option) => {
                                setSelectedSalesChannelId(option ? option.value : null);
                            }}
                            menuPosition={'fixed'}
                            validationKey={SALES_CHANNEL_VALIDATION_KEY}
                            validationLabel={T._('Online store', 'Admin')}
                            validate={(value) => productSource === 'channel' && !value}
                            validationMessage={T._('Online store is required.', 'Admin')}
                            isDisabled={isEnterpriseLocked}
                        />
                    </div>
                </SubSection>
            </Stack>
        </Dialog>
    );
};

const CreateStandaloneSiteDialog: FC<CreateStandaloneSiteDialogProps> = (props) => (
    <ValidationStateProvider>
        <CreateStandaloneSiteDialogContent {...props} />
    </ValidationStateProvider>
);

export default CreateStandaloneSiteDialog;
